import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import React from 'react'

interface SimpleTablePaginationProps {
  currentPage: number
  isLoading?: boolean
  onPageChange: (_page: number) => void
  onRefresh?: () => void
  pageSize: number
  totalItems: number
  totalPages: number
}

/**
 * Simple pagination component replicating the original inventory table footer design.
 */
export const SimpleTablePagination: React.FC<SimpleTablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  isLoading = false,
  onPageChange,
  onRefresh,
}) => {
  // Manejar el cambio de página con useCallback para evitar recreaciones
  const handlePageChange = React.useCallback(
    (page: number) => {
      // Evitar cambios de página durante la carga o si la página es la misma
      if (isLoading || page === currentPage) return
      onPageChange(page)
    },
    [currentPage, isLoading, onPageChange]
  )

  // Manejar el refresh
  const handleRefresh = React.useCallback(() => {
    if (isLoading || !onRefresh) return
    onRefresh()
  }, [isLoading, onRefresh])

  const renderPageButtons = () => {
    const pages: React.ReactNode[] = []
    const showEllipsis = totalPages > 7

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <Button
            aria-current={currentPage === i ? 'page' : undefined}
            aria-label={`Ir a la página ${i}`}
            className={`h-8 w-8 border-none p-0 ${
              currentPage === i
                ? 'bg-[#262626] text-white shadow-md hover:bg-[#262626] hover:text-white'
                : 'bg-transparent text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626] hover:shadow-md'
            }`}
            key={i}
            onClick={() => handlePageChange(i)}
            size="sm"
            variant="ghost"
          >
            {i}
          </Button>
        )
      }
    } else {
      // always show first page
      pages.push(
        <Button
          aria-current={currentPage === 1 ? 'page' : undefined}
          aria-label="Ir a la página 1"
          className={`h-8 w-8 border-none p-0 ${
            currentPage === 1
              ? 'bg-[#262626] text-white shadow-md hover:bg-[#262626] hover:text-white'
              : 'bg-transparent text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626] hover:shadow-md'
          }`}
          key={1}
          onClick={() => handlePageChange(1)}
          size="sm"
          variant="ghost"
        >
          1
        </Button>
      )

      // range around current page
      let startPage = Math.max(2, currentPage - 1)
      let endPage = Math.min(totalPages - 1, currentPage + 1)

      if (currentPage <= 3) {
        endPage = Math.min(5, totalPages - 1)
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 4)
      }

      if (startPage > 2) {
        pages.push(
          <span
            className="flex h-8 w-8 items-center justify-center text-sm text-[#262626]"
            key="ellipsis-start"
          >
            ...
          </span>
        )
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <Button
            aria-current={currentPage === i ? 'page' : undefined}
            aria-label={`Ir a la página ${i}`}
            className={`h-8 w-8 border-none p-0 ${
              currentPage === i
                ? 'bg-[#262626] text-white shadow-md hover:bg-[#262626] hover:text-white'
                : 'bg-transparent text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626] hover:shadow-md'
            }`}
            key={i}
            onClick={() => handlePageChange(i)}
            size="sm"
            variant="ghost"
          >
            {i}
          </Button>
        )
      }

      if (endPage < totalPages - 1) {
        pages.push(
          <span
            className="flex h-8 w-8 items-center justify-center text-sm text-[#262626]"
            key="ellipsis-end"
          >
            ...
          </span>
        )
      }

      if (totalPages > 1) {
        pages.push(
          <Button
            aria-current={currentPage === totalPages ? 'page' : undefined}
            aria-label={`Ir a la página ${totalPages}`}
            className={`h-8 w-8 border-none p-0 ${
              currentPage === totalPages
                ? 'bg-[#262626] text-white shadow-md hover:bg-[#262626] hover:text-white'
                : 'bg-transparent text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626] hover:shadow-md'
            }`}
            key={totalPages}
            onClick={() => handlePageChange(totalPages)}
            size="sm"
            variant="ghost"
          >
            {totalPages}
          </Button>
        )
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-between space-x-2 py-4">
      <div className="flex items-center gap-2">
        {/* Botón de refresh */}
        {onRefresh && (
          <Button
            aria-label="Refrescar datos"
            className={
              'flex h-8 w-8 items-center justify-center rounded-md' +
              ' border-none bg-transparent p-0 text-[#262626]' +
              ' transition-colors disabled:opacity-50' +
              ' hover:bg-[#FAFAFA] hover:text-[#262626]'
            }
            disabled={isLoading}
            onClick={handleRefresh}
            size="sm"
            title="Refrescar"
            variant="ghost"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>
        )}
        <div className="font-geist text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
          {!isLoading &&
            totalItems > 0 &&
            `Mostrando ${(currentPage - 1) * pageSize + 1}-${Math.min(
              currentPage * pageSize,
              totalItems
            )} de ${totalItems} resultados`}
        </div>
      </div>
      <div className="flex items-center space-x-1">
        <Button
          aria-label="Ir a la página anterior"
          className="h-8 w-8 border-none bg-transparent p-0 text-[#262626] hover:bg-transparent hover:text-[#262626]"
          disabled={currentPage === 1 || isLoading}
          onClick={() => handlePageChange(currentPage - 1)}
          size="sm"
          variant="ghost"
        >
          <span className="sr-only">Ir a la página anterior</span>
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {renderPageButtons()}

        <Button
          aria-label="Ir a la página siguiente"
          className="h-8 w-8 border-none bg-transparent p-0 text-[#262626] hover:bg-transparent hover:text-[#262626]"
          disabled={currentPage === totalPages || isLoading}
          onClick={() => handlePageChange(currentPage + 1)}
          size="sm"
          variant="ghost"
        >
          <span className="sr-only">Ir a la página siguiente</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default SimpleTablePagination
